/**
 * Rich media attachment handling for the Nova chat widget.
 *
 * Supports:
 *  - Paste (clipboard) for screenshots
 *  - Drag-and-drop
 *  - File picker via attach button
 *
 * Accepted types: images (png, jpg, gif), text files, logs.
 * Limits: 5 MB per file, 3 files per message.
 */

export interface FileAttachment {
  filename: string;
  contentType: string;
  data: string; // base64
  size: number;
}

const ACCEPTED_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'text/plain',
  'text/csv',
  'application/octet-stream', // .log files often report this
]);

const ACCEPTED_EXTENSIONS: ReadonlySet<string> = new Set([
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.txt',
  '.log',
  '.csv',
]);

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_FILES_PER_MESSAGE = 3;

/**
 * Determine whether a file is acceptable based on type and extension.
 */
function isAcceptedFile(file: File): boolean {
  if (ACCEPTED_TYPES.has(file.type)) return true;
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return ACCEPTED_EXTENSIONS.has(ext);
}

/**
 * Read a File as a base64 data string (without the data-URL prefix).
 */
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the "data:...;base64," prefix
      const base64 = result.split(',')[1] ?? '';
      resolve(base64);
    };
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsDataURL(file);
  });
}

/**
 * Process a list of Files into FileAttachments, enforcing size and count limits.
 * Returns only the valid files (up to MAX_FILES_PER_MESSAGE).
 */
async function processFiles(
  files: File[],
  existingCount: number,
): Promise<FileAttachment[]> {
  const remaining = MAX_FILES_PER_MESSAGE - existingCount;
  if (remaining <= 0) return [];

  const validFiles = files
    .filter((f) => isAcceptedFile(f) && f.size <= MAX_FILE_SIZE)
    .slice(0, remaining);

  const attachments: FileAttachment[] = [];
  for (const file of validFiles) {
    const data = await readFileAsBase64(file);
    attachments.push({
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      data,
      size: file.size,
    });
  }
  return attachments;
}

/**
 * Create inline preview elements for a list of attachments.
 */
export function createAttachmentPreviews(
  attachments: FileAttachment[],
  onRemove: (index: number) => void,
): HTMLElement {
  const container = document.createElement('div');
  container.className = 'nova-attachment-previews';
  container.style.display = 'flex';
  container.style.gap = '6px';
  container.style.flexWrap = 'wrap';
  container.style.padding = '4px 0';

  attachments.forEach((attachment, idx) => {
    const badge = document.createElement('div');
    badge.className = 'nova-attachment-badge';
    badge.style.display = 'inline-flex';
    badge.style.alignItems = 'center';
    badge.style.gap = '4px';
    badge.style.padding = '4px 8px';
    badge.style.borderRadius = '4px';
    badge.style.fontSize = '12px';
    badge.style.background = 'var(--nova-bg-secondary)';
    badge.style.color = 'var(--nova-text-secondary)';

    if (attachment.contentType.startsWith('image/')) {
      const img = document.createElement('img');
      img.src = `data:${attachment.contentType};base64,${attachment.data}`;
      img.alt = attachment.filename;
      img.style.width = '24px';
      img.style.height = '24px';
      img.style.objectFit = 'cover';
      img.style.borderRadius = '2px';
      badge.appendChild(img);
    }

    const nameSpan = document.createElement('span');
    const sizeKB = Math.ceil(attachment.size / 1024);
    nameSpan.textContent = `${attachment.filename} (${sizeKB}KB)`;
    badge.appendChild(nameSpan);

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.setAttribute('aria-label', `Remove ${attachment.filename}`);
    removeBtn.textContent = '\u00D7';
    removeBtn.style.border = 'none';
    removeBtn.style.background = 'none';
    removeBtn.style.cursor = 'pointer';
    removeBtn.style.color = 'var(--nova-text-secondary)';
    removeBtn.style.fontSize = '14px';
    removeBtn.style.lineHeight = '1';
    removeBtn.addEventListener('click', () => onRemove(idx));
    badge.appendChild(removeBtn);

    container.appendChild(badge);
  });

  return container;
}

/**
 * Wire up rich media handlers on an input area element.
 *
 * @param inputArea - The `.nova-input-area` container element.
 * @param onAttach  - Callback with newly attached files.
 */
export function setupRichMedia(
  inputArea: HTMLElement,
  onAttach: (files: FileAttachment[]) => void,
): void {
  let currentCount = 0;

  // --- Paste handler (clipboard screenshots) ---
  inputArea.addEventListener('paste', (e: Event) => {
    const clipboardEvent = e as ClipboardEvent;
    const clipboardData = clipboardEvent.clipboardData;
    if (!clipboardData) return;

    const files: File[] = [];
    for (let i = 0; i < clipboardData.items.length; i++) {
      const item = clipboardData.items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }

    if (files.length > 0) {
      clipboardEvent.preventDefault();
      void processFiles(files, currentCount).then((attachments) => {
        if (attachments.length > 0) {
          currentCount += attachments.length;
          onAttach(attachments);
        }
      });
    }
  });

  // --- Drag-and-drop handler ---
  inputArea.addEventListener('dragover', (e: Event) => {
    const dragEvent = e as DragEvent;
    dragEvent.preventDefault();
    inputArea.style.outline = '2px dashed var(--nova-brand)';
  });

  inputArea.addEventListener('dragleave', () => {
    inputArea.style.outline = '';
  });

  inputArea.addEventListener('drop', (e: Event) => {
    const dragEvent = e as DragEvent;
    dragEvent.preventDefault();
    inputArea.style.outline = '';

    const dt = dragEvent.dataTransfer;
    if (!dt) return;

    const files: File[] = Array.from(dt.files);
    if (files.length > 0) {
      void processFiles(files, currentCount).then((attachments) => {
        if (attachments.length > 0) {
          currentCount += attachments.length;
          onAttach(attachments);
        }
      });
    }
  });

  // --- File picker (attach button) ---
  const attachBtn = inputArea.querySelector('.nova-attach-btn');
  if (attachBtn) {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.accept = Array.from(ACCEPTED_EXTENSIONS).join(',');
    fileInput.style.display = 'none';

    inputArea.appendChild(fileInput);

    attachBtn.addEventListener('click', () => {
      fileInput.click();
    });

    fileInput.addEventListener('change', () => {
      const files = Array.from(fileInput.files ?? []);
      if (files.length > 0) {
        void processFiles(files, currentCount).then((attachments) => {
          if (attachments.length > 0) {
            currentCount += attachments.length;
            onAttach(attachments);
          }
        });
      }
      // Reset so the same file can be selected again
      fileInput.value = '';
    });
  }

  /**
   * Expose a reset function so the widget can reset the count after sending.
   * Attached as a property on the inputArea element.
   */
  (inputArea as HTMLElement & { _novaResetAttachmentCount?: () => void })._novaResetAttachmentCount =
    () => {
      currentCount = 0;
    };
}
